import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Layers, Pencil, ChevronRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface SpecTemplate {
  id: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  isActive: boolean;
}

interface SpecField {
  id: string;
  templateId: string;
  fieldKey: string;
  label: string;
  fieldType: string;
  uom: string | null;
  options: string[] | null;
  isRequired: boolean;
  sortOrder: number;
  helpText: string | null;
}

interface SpecTemplateDetail extends SpecTemplate {
  fields: SpecField[];
}

interface Category {
  id: string;
  name: string;
}

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "select", label: "Select" },
  { value: "boolean", label: "Yes/No" },
  { value: "date", label: "Date" },
];

export default function SpecificationsSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [tplDialog, setTplDialog] = useState(false);
  const [editingTpl, setEditingTpl] = useState<SpecTemplate | null>(null);
  const [tplForm, setTplForm] = useState({
    name: "", description: "", categoryId: "__all__", isActive: true,
  });

  const [activeTplId, setActiveTplId] = useState<string | null>(null);
  const [fieldDialog, setFieldDialog] = useState(false);
  const [fieldForm, setFieldForm] = useState({
    fieldKey: "", label: "", fieldType: "text",
    uom: "", options: "", isRequired: false, sortOrder: 0, helpText: "",
  });

  const { data: templates = [] } = useQuery<SpecTemplate[]>({
    queryKey: ["/api/spec-templates"],
    queryFn: () => api.get<SpecTemplate[]>(`/spec-templates`, sessionId),
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
    queryFn: () => api.get<Category[]>(`/categories`, sessionId),
  });

  const { data: activeTpl } = useQuery<SpecTemplateDetail>({
    queryKey: ["/api/spec-templates", activeTplId],
    queryFn: () => api.get<SpecTemplateDetail>(`/spec-templates/${activeTplId}`, sessionId),
    enabled: !!activeTplId,
  });

  const saveTplMutation = useMutation({
    mutationFn: () => {
      const body = {
        ...tplForm,
        categoryId: tplForm.categoryId === "__all__" ? null : tplForm.categoryId,
      };
      return editingTpl
        ? api.patch(`/spec-templates/${editingTpl.id}`, body, sessionId)
        : api.post(`/spec-templates`, body, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/spec-templates"] });
      setTplDialog(false);
      setEditingTpl(null);
      toast({ title: editingTpl ? "Template updated" : "Template created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteTplMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/spec-templates/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/spec-templates"] });
      setActiveTplId(null);
      toast({ title: "Template deleted" });
    },
  });

  const saveFieldMutation = useMutation({
    mutationFn: () => {
      const body = {
        fieldKey: fieldForm.fieldKey,
        label: fieldForm.label,
        fieldType: fieldForm.fieldType,
        uom: fieldForm.uom || null,
        options: fieldForm.fieldType === "select" && fieldForm.options
          ? fieldForm.options.split(",").map(s => s.trim()).filter(Boolean)
          : null,
        isRequired: fieldForm.isRequired,
        sortOrder: fieldForm.sortOrder,
        helpText: fieldForm.helpText || null,
      };
      return api.post(`/spec-templates/${activeTplId}/fields`, body, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/spec-templates", activeTplId] });
      setFieldDialog(false);
      toast({ title: "Field added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (fieldId: string) =>
      api.delete(`/spec-templates/${activeTplId}/fields/${fieldId}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/spec-templates", activeTplId] });
      toast({ title: "Field deleted" });
    },
  });

  const handleNew = () => {
    setEditingTpl(null);
    setTplForm({ name: "", description: "", categoryId: "__all__", isActive: true });
    setTplDialog(true);
  };

  const handleEdit = (t: SpecTemplate) => {
    setEditingTpl(t);
    setTplForm({
      name: t.name, description: t.description || "",
      categoryId: t.categoryId || "__all__",
      isActive: t.isActive,
    });
    setTplDialog(true);
  };

  const handleAddField = () => {
    setFieldForm({
      fieldKey: "", label: "", fieldType: "text",
      uom: "", options: "", isRequired: false,
      sortOrder: activeTpl?.fields.length || 0,
      helpText: "",
    });
    setFieldDialog(true);
  };

  const getCategoryName = (id: string | null) => {
    if (!id) return null;
    return categories.find(c => c.id === id)?.name || null;
  };

  // Detail view
  if (activeTplId && activeTpl) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={activeTpl.name}
          description={activeTpl.description || "Manage fields for this specification template"}
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setActiveTplId(null)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> All Templates
              </Button>
              <Button onClick={handleAddField}>
                <Plus className="h-4 w-4 mr-1" /> Add Field
              </Button>
            </div>
          }
        />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="h-4 w-4" /> Fields ({activeTpl.fields.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeTpl.fields.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No fields defined yet. Add your first spec field.
              </p>
            ) : (
              <div className="space-y-2">
                {activeTpl.fields.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{f.label}</span>
                        <Badge variant="outline" className="text-[10px] font-mono">{f.fieldKey}</Badge>
                        <Badge className="text-[10px]">{f.fieldType}</Badge>
                        {f.uom && <Badge variant="secondary" className="text-[10px]">{f.uom}</Badge>}
                        {f.isRequired && <Badge variant="destructive" className="text-[10px]">Required</Badge>}
                      </div>
                      {f.helpText && (
                        <p className="text-xs text-muted-foreground mt-0.5">{f.helpText}</p>
                      )}
                      {f.options && f.options.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Options: {f.options.join(", ")}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        if (confirm(`Delete field "${f.label}"?`)) deleteFieldMutation.mutate(f.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={fieldDialog} onOpenChange={setFieldDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Spec Field</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Label *</label>
                <Input
                  value={fieldForm.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    setFieldForm({
                      ...fieldForm,
                      label,
                      fieldKey: fieldForm.fieldKey || label
                        .toLowerCase().trim()
                        .replace(/[^a-z0-9\s]/g, "")
                        .replace(/\s+/g, "_"),
                    });
                  }}
                  placeholder="e.g. Box Length"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Field Key *</label>
                <Input
                  className="font-mono"
                  value={fieldForm.fieldKey}
                  onChange={(e) => setFieldForm({ ...fieldForm, fieldKey: e.target.value })}
                  placeholder="box_length"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Type</label>
                  <select
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={fieldForm.fieldType}
                    onChange={(e) => setFieldForm({ ...fieldForm, fieldType: e.target.value })}
                  >
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Unit of Measure</label>
                  <Input
                    value={fieldForm.uom}
                    onChange={(e) => setFieldForm({ ...fieldForm, uom: e.target.value })}
                    placeholder="e.g. in, lb, kg"
                  />
                </div>
              </div>
              {fieldForm.fieldType === "select" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Options (comma-separated)</label>
                  <Input
                    value={fieldForm.options}
                    onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                    placeholder="Option 1, Option 2, Option 3"
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">Help Text</label>
                <Textarea
                  value={fieldForm.helpText}
                  onChange={(e) => setFieldForm({ ...fieldForm, helpText: e.target.value })}
                  rows={2}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={fieldForm.isRequired}
                  onChange={(e) => setFieldForm({ ...fieldForm, isRequired: e.target.checked })}
                />
                Required
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setFieldDialog(false)}>Cancel</Button>
                <Button
                  onClick={() => saveFieldMutation.mutate()}
                  disabled={!fieldForm.label || !fieldForm.fieldKey || saveFieldMutation.isPending}
                >
                  {saveFieldMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Add Field
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <PageHeader
        title="Specification Templates"
        description="Define product specification templates with custom fields for different product categories"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew}>
              <Plus className="h-4 w-4 mr-1" /> Add Template
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4" /> All Templates ({templates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No specification templates yet. Create your first template to define spec fields.
            </p>
          ) : (
            <div className="space-y-2">
              {templates.map((t) => {
                const catName = getCategoryName(t.categoryId);
                return (
                  <div
                    key={t.id}
                    className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{t.name}</span>
                        {catName ? (
                          <Badge variant="outline" className="text-[10px]">{catName}</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">All categories</Badge>
                        )}
                        {!t.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                      </div>
                      {t.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => setActiveTplId(t.id)}
                        title="Manage fields"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(t)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          if (confirm(`Delete template "${t.name}"?`)) deleteTplMutation.mutate(t.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={tplDialog} onOpenChange={setTplDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTpl ? "Edit Spec Template" : "Create Spec Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={tplForm.name}
                onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                placeholder="e.g., Corrugated Box Specs"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={tplForm.description}
                onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select
                value={tplForm.categoryId}
                onValueChange={(v) => setTplForm({ ...tplForm, categoryId: v })}
              >
                <SelectTrigger><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={tplForm.isActive}
                onChange={(e) => setTplForm({ ...tplForm, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTplDialog(false)}>Cancel</Button>
              <Button
                onClick={() => saveTplMutation.mutate()}
                disabled={!tplForm.name || saveTplMutation.isPending}
              >
                {saveTplMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingTpl ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
